// src/app/api/learningApi.ts
//
// Online learning:
//   • Learn  — /learn/*   parents (for their children) and students
//   • Review — /review/*  tutors and admins

import { baseApi } from "./baseApi";

/* ─────────────────────────────────────────────────────────────────
   SHARED TYPES
───────────────────────────────────────────────────────────────── */

export type WorkStatus = "not_started" | "in_progress" | "submitted" | "needs_revision" | "accepted";
export type EvidenceType = "photo" | "video" | "audio" | "link" | "file" | "text" | "project";

export interface Evidence {
  id?: string;
  type: EvidenceType;
  key?: string | null;
  url?: string | null;
  text?: string | null;
  fileName?: string | null;
  contentType?: string | null;
  /** type "project": a copy of an in-app workspace project, made when handed in */
  projectId?: string | null;
  projectKind?: "scratch" | "web" | "react" | "native" | null;
}

export interface Reflection {
  built?: string | null;
  helps?: string | null;
  learned?: string | null;
}

export type Scores = Partial<Record<string, number>>;

export interface Review {
  decision: "accepted" | "needs_revision";
  glow: string | null;
  grow: string | null;
  nextStep: string | null;
  hasVoiceNote?: boolean;
  voiceNoteKey: string | null;
  scores: Scores;
  reviewerName: string | null;
  reviewedAt: string;
}

export interface Attempt {
  id: string;
  number: number;
  submittedAt: string;
  reflection: Reflection;
  evidence: Evidence[];
  review: Review | null;
}

export interface WeekProgress {
  total: number;
  submitted: number;
  accepted: number;
  needsRevision: number;
  allSubmitted: boolean;
  targetMet: boolean;
}

export interface LearnWeekRef {
  key: string;
  term: string;
  weekNumber: number;
  title: string;
  topics: string | null;
  isShowcase: boolean;
  isCurrent: boolean;
  calendarWeek: number;
}

export interface LearnerChild {
  id: string;
  firstName: string;
  lastName: string;
  age: number;
  track: string;
  active: boolean;
  calendarWeek?: number;
  weekEndsAt?: string;
  currentWeek?: LearnWeekRef;
  progress?: WeekProgress;
  weeksBehind?: number;
  unseenFeedback?: number;
  /** The free first week: running (active), or whether it can still be started */
  trial?: { active: boolean; endsAt: string | null; eligible?: boolean; ended?: boolean; reason?: string | null } | null;
}

export interface Journey {
  child: { id: string; firstName: string; lastName: string; age: number; track: string };
  calendarWeek: number;
  weekEndsAt: string;
  weeksBehind: number;
  /** Set during the free first week */
  trial?: { active: boolean; endsAt: string } | null;
  weeks: (LearnWeekRef & { lessonsReady: boolean; progress: WeekProgress })[];
  nextWeek: { title: string; opensAt: string } | null;
}

export interface LearnLesson {
  key: string;
  order: number;
  strand: string;
  title: string;
  objective: string | null;
  watchMinutes: number;
  doMinutes: number;
  uploadMinutes: number;
  hasVideo: boolean;
  /** The video's real length, when it has one (watchMinutes is the plan) */
  videoMinutes: number | null;
  videoDurationSec: number | null;
  transcript: string[];
  action: { steps: string[]; materials: string[]; offScreen: boolean };
  evidence: { types: EvidenceType[]; prompt: string | null };
  parentNote: string | null;
  /** The in-app workspace the Do step offers */
  workspace: "scratch" | "web" | "react" | "native" | null;
  work: {
    status: WorkStatus;
    watchedAt?: string | null;
    feedbackSeenAt?: string | null;
    attempts: Attempt[];
  };
}

export interface LearnWeek extends LearnWeekRef {
  weekEndsAt: string | null;
  weeklyTarget: { must: string[]; stretch: string[] };
  parentBrief: string | null;
  homeMaterials: string[];
  progress: WeekProgress;
  lessons: LearnLesson[];
  storageConfigured: boolean;
  /** Hybrid learners: this is an on-site week (where and when) */
  onsiteSession?: { city: string; name: string | null; address: string | null; sessionDay: string | null; sessionTime: string | null } | null;
}

export interface FeedbackItem {
  lessonKey: string;
  weekKey: string;
  lessonTitle: string;
  strand: string | null;
  status: WorkStatus;
  attempts: number;
  unseen: boolean;
  review: Review | null;
}

/* Review side */

export interface QueueItem {
  id: string;
  status: WorkStatus;
  track: string;
  weekKey: string;
  child: { id: string; firstName: string; lastInitial: string; age: number } | null;
  lesson: { key: string; title?: string; strand?: string };
  attemptNumber: number;
  evidenceTypes: EvidenceType[];
  submittedAt: string | null;
  waitingHours: number | null;
  overdue: boolean;
  reviewedAt: string | null;
  reviewerName: string | null;
}

export interface ReviewStats {
  waiting: number;
  overdue: number;
  byTrack: Record<string, number>;
  reviewedLast30Days: number;
  averageHoursToReview: number | null;
  targetHours: number;
}

export interface SubmissionDetail {
  id: string;
  status: WorkStatus;
  track: string;
  child: { id: string; firstName: string; lastInitial: string; age: number; track: string } | null;
  week: { key: string; title: string; term: string; weekNumber: number; mustDo: string[] } | null;
  lesson: {
    key: string;
    title: string;
    strand: string;
    objective: string | null;
    steps: string[];
    evidencePrompt: string | null;
    rubricFocus: string[];
  } | null;
  watchedAt: string | null;
  attempts: Attempt[];
  recentWork: { lessonKey: string; lessonTitle: string; status: WorkStatus; attempts: number; lastDecision: string | null }[];
  storageConfigured: boolean;
}

interface Ok<T> {
  success: boolean;
  message?: string;
  data: T;
}

/* ─────────────────────────────────────────────────────────────────
   API
───────────────────────────────────────────────────────────────── */

/** A parent's view of one child's FSL AI key (ages 9–12). */
export interface AiKeyRow {
  childId: string;
  firstName: string;
  eligible: boolean;
  key: { prefix: string; createdAt: string; lastUsedAt: string | null } | null;
  usedToday: number;
  dailyLimit: number;
  switchedOn: boolean;
  newKey?: string;
}

/** A parent's view of one child's learner login (ages 9+). */
export interface LearnerLoginRow {
  childId: string;
  firstName: string;
  track: string;
  eligible: boolean;
  login: { username: string; createdAt: string | null; lastLoginAt: string | null } | null;
  suggestion: string | null;
}

export const learningApi = baseApi
  .enhanceEndpoints({ addTagTypes: ["Learn", "LearnWeek", "Review", "LearnerLogins", "AiKeys"] })
  .injectEndpoints({
    endpoints: (b) => ({
      // ── Learn ────────────────────────────────────────────────────
      getLearners: b.query<Ok<LearnerChild[]>, void>({
        query: () => "/learn/children",
        providesTags: ["Learn"],
      }),
      getJourney: b.query<Ok<Journey>, string>({
        query: (childId) => `/learn/${childId}`,
        providesTags: ["Learn"],
      }),
      getLearnWeek: b.query<Ok<LearnWeek>, { childId: string; weekKey: string }>({
        query: ({ childId, weekKey }) => `/learn/${childId}/weeks/${weekKey}`,
        providesTags: (_r, _e, a) => [{ type: "LearnWeek", id: `${a.childId}:${a.weekKey}` }],
      }),
      startTrial: b.mutation<Ok<{ startedAt: string; endsAt: string }>, string>({
        query: (childId) => ({ url: `/learn/${childId}/trial`, method: "POST" }),
        invalidatesTags: ["Learn"],
      }),
      markWatched: b.mutation<Ok<null>, { childId: string; lessonKey: string }>({
        query: ({ childId, lessonKey }) => ({
          url: `/learn/${childId}/lessons/${lessonKey}/watched`,
          method: "POST",
        }),
      }),
      getLessonVideoUrl: b.mutation<
        Ok<{
          url: string;
          quality: "low" | "high";
          hasLowRes: boolean;
          captionsUrl: string | null;
          posterUrl: string | null;
        }>,
        { childId: string; lessonKey: string; quality?: "low" }
      >({
        query: ({ childId, lessonKey, quality }) => ({
          url: `/learn/${childId}/lessons/${lessonKey}/video-url${quality ? `?quality=${quality}` : ""}`,
          method: "GET",
        }),
      }),
      getEvidenceUploadUrl: b.mutation<
        Ok<{ uploadUrl: string; key: string }>,
        { childId: string; lessonKey: string; type: EvidenceType; fileName: string; contentType: string; sizeBytes: number }
      >({
        query: ({ childId, lessonKey, ...body }) => ({
          url: `/learn/${childId}/lessons/${lessonKey}/upload-url`,
          method: "POST",
          body,
        }),
      }),
      submitWork: b.mutation<
        Ok<LearnLesson["work"]>,
        { childId: string; weekKey: string; lessonKey: string; evidence: Evidence[]; reflection: Reflection }
      >({
        query: ({ childId, lessonKey, evidence, reflection }) => ({
          url: `/learn/${childId}/lessons/${lessonKey}/submit`,
          method: "POST",
          body: { evidence, reflection },
        }),
        invalidatesTags: (_r, _e, a) => [
          { type: "LearnWeek", id: `${a.childId}:${a.weekKey}` },
          "Learn",
          "Review",
        ],
      }),
      markFeedbackSeen: b.mutation<Ok<null>, { childId: string; weekKey: string; lessonKey: string }>({
        query: ({ childId, lessonKey }) => ({
          url: `/learn/${childId}/lessons/${lessonKey}/feedback-seen`,
          method: "POST",
        }),
        invalidatesTags: ["Learn"],
      }),
      getLearnMediaUrl: b.mutation<Ok<{ url: string }>, { childId: string; lessonKey: string; key: string }>({
        query: ({ childId, lessonKey, key }) => ({
          url: `/learn/${childId}/media-url?lessonKey=${encodeURIComponent(lessonKey)}&key=${encodeURIComponent(key)}`,
          method: "GET",
        }),
      }),
      getFeedback: b.query<Ok<FeedbackItem[]>, string>({
        query: (childId) => `/learn/${childId}/feedback`,
        providesTags: ["Learn"],
      }),
      sendStuck: b.mutation<
        Ok<null>,
        { childId: string; lessonKey: string; tried: string; errorText?: string; lookedUp: boolean }
      >({
        query: ({ childId, ...body }) => ({ url: `/learn/${childId}/stuck`, method: "POST", body }),
      }),

      // ── Learner logins (parent sets them up for ages 9+) ─────────
      getLearnerLogins: b.query<Ok<LearnerLoginRow[]>, void>({
        query: () => "/parent/learner-logins",
        providesTags: ["LearnerLogins"],
      }),
      saveLearnerLogin: b.mutation<
        Ok<LearnerLoginRow> & { message: string },
        { childId: string; username?: string; password?: string }
      >({
        query: ({ childId, ...body }) => ({
          url: `/parent/learner-logins/${childId}`,
          method: "PUT",
          body,
        }),
        invalidatesTags: ["LearnerLogins"],
      }),
      getAiKeys: b.query<Ok<AiKeyRow[]>, void>({
        query: () => "/parent/ai-keys",
        providesTags: ["AiKeys"],
      }),
      createAiKey: b.mutation<Ok<AiKeyRow> & { message: string }, string>({
        query: (childId) => ({ url: `/parent/ai-keys/${childId}`, method: "POST" }),
        invalidatesTags: ["AiKeys"],
      }),
      removeAiKey: b.mutation<Ok<AiKeyRow> & { message: string }, string>({
        query: (childId) => ({ url: `/parent/ai-keys/${childId}`, method: "DELETE" }),
        invalidatesTags: ["AiKeys"],
      }),
      removeLearnerLogin: b.mutation<Ok<LearnerLoginRow> & { message?: string }, string>({
        query: (childId) => ({ url: `/parent/learner-logins/${childId}`, method: "DELETE" }),
        invalidatesTags: ["LearnerLogins"],
      }),

      // ── Review ───────────────────────────────────────────────────
      getReviewStats: b.query<Ok<ReviewStats>, void>({
        query: () => "/review/stats",
        providesTags: ["Review"],
      }),
      getReviewQueue: b.query<
        Ok<QueueItem[]> & { total: number; page: number; pages: number },
        { status?: string; track?: string; page?: number }
      >({
        query: ({ status = "submitted", track = "", page = 1 }) =>
          `/review/queue?status=${status}&track=${track}&page=${page}`,
        providesTags: ["Review"],
      }),
      getSubmission: b.query<Ok<SubmissionDetail>, string>({
        query: (id) => `/review/submissions/${id}`,
        providesTags: (_r, _e, id) => [{ type: "Review", id }],
      }),
      getReviewMediaUrl: b.mutation<Ok<{ url: string }>, { id: string; key: string }>({
        query: ({ id, key }) => ({
          url: `/review/submissions/${id}/media-url?key=${encodeURIComponent(key)}`,
          method: "GET",
        }),
      }),
      getVoiceUploadUrl: b.mutation<
        Ok<{ uploadUrl: string; key: string }>,
        { id: string; fileName: string; contentType: string }
      >({
        query: ({ id, ...body }) => ({
          url: `/review/submissions/${id}/voice-upload-url`,
          method: "POST",
          body,
        }),
      }),
      sendReview: b.mutation<
        Ok<{ status: WorkStatus }>,
        {
          id: string;
          decision: "accepted" | "needs_revision";
          glow: string;
          grow: string;
          nextStep: string;
          scores: Scores;
          voiceNoteKey?: string | null;
        }
      >({
        query: ({ id, ...body }) => ({
          url: `/review/submissions/${id}/review`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["Review"],
      }),
      getCommentBank: b.query<Ok<{ glow: string[]; grow: string[]; nextStep: string[] }>, string>({
        query: (lessonKey) => `/review/comment-bank?lessonKey=${encodeURIComponent(lessonKey)}`,
      }),
    }),
  });

export const {
  useGetLearnersQuery,
  useGetJourneyQuery,
  useGetLearnWeekQuery,
  useMarkWatchedMutation,
  useStartTrialMutation,
  useGetLessonVideoUrlMutation,
  useGetEvidenceUploadUrlMutation,
  useSubmitWorkMutation,
  useMarkFeedbackSeenMutation,
  useGetLearnMediaUrlMutation,
  useGetFeedbackQuery,
  useSendStuckMutation,
  useGetLearnerLoginsQuery,
  useSaveLearnerLoginMutation,
  useRemoveLearnerLoginMutation,
  useGetAiKeysQuery,
  useCreateAiKeyMutation,
  useRemoveAiKeyMutation,
  useGetReviewStatsQuery,
  useGetReviewQueueQuery,
  useGetSubmissionQuery,
  useGetReviewMediaUrlMutation,
  useGetVoiceUploadUrlMutation,
  useSendReviewMutation,
  useGetCommentBankQuery,
} = learningApi;

/* ─────────────────────────────────────────────────────────────────
   HELPERS shared by the Learn and Review pages
───────────────────────────────────────────────────────────────── */

/** PUT a file straight to storage, reporting progress 0–100. */
export function putFile(
  url: string,
  file: Blob,
  contentType: string,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error("Upload failed — check your connection and try again"));
    xhr.send(file);
  });
}

export const RUBRIC_LABELS: Record<string, string> = {
  technical_quality: "Technical quality",
  understanding: "Understanding",
  user_value: "User value",
  iteration: "Iteration",
  communication: "Communication",
  character: "Character",
};

export const SCORE_LABELS: Record<number, string> = {
  1: "Emerging",
  2: "Developing",
  3: "Strong",
};

export function errorText(err: unknown, fallback: string) {
  const e = err as { data?: { error?: string }; message?: string };
  return e?.data?.error ?? e?.message ?? fallback;
}
